'use client'

/**
 * Purpose: Survey final page — consent checkbox (questionnaire) or biometric note (photo).
 * Inputs: formState, actionError from submitSurvey, locale, and mode.
 * Outputs: Consent UI and server validation error display.
 */

import type { Dispatch, SetStateAction, ReactNode } from 'react'
import Link from 'next/link'
import type { FormState, ActionError } from '@lib/models/types'

// next-intl rich() wrapper — embeds Link components in translated strings.
type RichTranslator = {
  (key: string): string
  rich: (
    key: string,
    values: Record<string, (chunks: ReactNode) => ReactNode>,
  ) => ReactNode
}

// Last survey step — consent gate before submitSurvey runs.
export function SurveyConsentPage({
  formState,
  setFormState,
  actionError,
  locale,
  mode,
  title,
  description,
  consentNote,
  tPages,
}: {
  formState: FormState
  setFormState: Dispatch<SetStateAction<FormState>>
  actionError: ActionError | null
  locale: string
  mode: 'survey' | 'photo'
  // Copy props from SurveyForm — title, description, and rich checkbox label.
  title: string
  description: string
  consentNote: string
  tPages: RichTranslator
}) {
  // Final page — checkbox consent for questionnaire, note-only for photo path.
  const consentCheckboxLabel = tPages.rich('consent.checkboxLabel', {
    termsLink: (chunks) => (
      <Link
        href={`/${locale}/terms`}
        className="text-orange hover:opacity-80 underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {chunks}
      </Link>
    ),
    // Privacy policy link rendered inside the rich consent checkbox label.
    privacyLink: (chunks) => (
      <Link
        href={`/${locale}/privacy`}
        className="text-orange hover:opacity-80 underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {chunks}
      </Link>
    ),
  })

  // Photo path shows a note only; questionnaire path requires an explicit checkbox.
  return (
    <div id="survey-consent-page">
      <h1 id="survey-consent-page-title" className="mb-4">
        {title}
      </h1>
      <p id="survey-consent-page-description" className="text-black mb-6 text-sm">
        {description}
      </p>
      {mode === 'photo' ? (
        <p id="survey-consent-photo-note" className="text-black/60 text-xs italic">
          {consentNote}
        </p>
      ) : (
        <>
          {/* Questionnaire path — consent checkbox gates the submit button in SurveyForm. */}
          <label id="survey-consent-checkbox-label" className="flex items-start gap-3 cursor-pointer">
          <input
            id="survey-consent-checkbox"
            type="checkbox"
            checked={formState.consentGiven}
            onChange={(e) => {
              // Persist consent flag — submit button stays disabled until checked.
              setFormState((s) => ({ ...s, consentGiven: e.target.checked }))
            }}
            className="mt-1 accent-orange"
          />
          <span className="text-black text-sm">
            {consentCheckboxLabel}
          </span>
        </label>
        </>
      )}
      {/* Server-side submit errors from submitSurvey action. */}
      {actionError?.error && (
        <p id="survey-consent-action-error" role="alert" className="text-red-600 mt-4 text-sm">
          {actionError.error}
        </p>
      )}
      {actionError?.errors?.formErrors?.map((e, i) => (
        <p key={i} role="alert" className="text-red-600 mt-2 text-sm">
          {e}
        </p>
      ))}
      {/* Per-field validation messages returned by the server. */}
      {actionError?.errors?.fieldErrors &&
        Object.entries(actionError.errors.fieldErrors).map(([field, errs]) =>
          (errs as string[]).map((e, i) => (
            <p key={`${field}-${i}`} role="alert" className="text-red-600 mt-2 text-sm">
              {field}: {e}
            </p>
          )),
        )}
    </div>
  )
}
